/**
 * Markdown Editor Component
 * Simple textarea with live markdown preview
 * Supports basic markdown formatting for admin welcome messages
 */

import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/utils/classNames';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  className?: string;
  minHeight?: string;
}

export function MarkdownEditor({
  value,
  onChange,
  placeholder = 'Enter text...',
  label,
  className = '',
  minHeight = '100px',
}: MarkdownEditorProps) {
  const [showPreview, setShowPreview] = useState(false);

  return (
    <div className={cn('space-y-2', className)}>
      {/* Label and Preview Toggle */}
      <div className="flex items-center justify-between">
        {label && (
          <label className="block text-sm font-medium text-text-primary">
            {label}
          </label>
        )}
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className="flex items-center gap-1 rounded-md px-2 py-1
            text-xs font-medium text-text-secondary
            hover:bg-bg-secondary transition-colors"
        >
          {showPreview ? (
            <>
              <EyeOff className="h-3 w-3" />
              Edit
            </>
          ) : (
            <>
              <Eye className="h-3 w-3" />
              Preview
            </>
          )}
        </button>
      </div>

      {/* Editor or Preview */}
      {showPreview ? (
        <div
          className="rounded-lg border border-border-default
            bg-bg-primary px-3 py-2 text-text-primary
            prose prose-sm max-w-none"
          style={{ minHeight }}
        >
          <ReactMarkdown>{value || placeholder}</ReactMarkdown>
        </div>
      ) : (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-lg border border-border-default
            bg-bg-primary px-3 py-2 text-text-primary
            placeholder:text-text-tertiary
            focus:border-stellar-primary focus:outline-none
            focus:ring-2 focus:ring-stellar-primary/20
            transition-colors resize-y"
          style={{ minHeight }}
        />
      )}

      {/* Markdown Tips */}
      {!showPreview && (
        <div className="text-xs text-text-tertiary">
          <span className="font-medium">Markdown supported:</span>
          {' '}
          **bold**, *italic*, [link](url), `code`
        </div>
      )}
    </div>
  );
}
